import { skipToken } from '@reduxjs/toolkit/query';
import { useMemo } from 'react';

import creditProgressMessagesDefaults from '../datasource/defaults/creditProgressMessages.json';
import { CleTimeRecord, DailySegment, LaunchSegment } from '../datasource/generated';
import { useGetCleTimeRecordsQuery } from '../datasource/queries/cle';
import { useGetCreditProgressMessagesQuery } from '../datasource/queries/cmsMessages';
import { EndOfVideoViewState, IEndOfVideoProps, RequestCreditViewState } from '../stories/EndOfVideoPanel/EndOfVideoPanel';
import { normalizeCalendarDate } from '../utility/calendarDate';
import { useAppSelector } from '.';
import useCpeExams from './useCpeExams';
import { useProgramDetails } from './useProgramDetails';

export const hasIncompleteProgramCredit = (timeRecords: CleTimeRecord[] | undefined) => {
    const progressBySegment = new Map<number, number>();

    timeRecords
        ?.filter(record => record.trackCle && record.prgSegment_SK)
        .forEach(record => {
            const segmentSk = record.prgSegment_SK as number;
            const currentProgress = progressBySegment.get(segmentSk) ?? 0;
            progressBySegment.set(segmentSk, Math.max(currentProgress, record.percentComplete ?? 0));
        });

    return progressBySegment.size > 0
        && Array.from(progressBySegment.values()).some(percentComplete => percentComplete < 100);
};

interface GetEndOfVideoViewStateProps {
    programFormat: IEndOfVideoProps['programFormat'];
    hasNextSegment: boolean;
    isOnFinalLiveProgramDay: boolean;
}

interface GetRequestCreditViewStateProps {
    programFormat: IEndOfVideoProps['programFormat'];
    isGroupcast: boolean;
    encryptedRegistrationId?: string;
    isCreditProgressError: boolean;
    isCreditProgressLoading: boolean;
    timeRecords?: CleTimeRecord[];
}

export const computeIsFinalLiveProgramDay = (
    dailySegments: DailySegment[] | null | undefined,
    now = new Date(),
) => {
    const finalDailySegment = dailySegments?.[dailySegments.length - 1];
    const finalDayStartTimeUtc = finalDailySegment?.segments?.[0]?.startTimeUtc;
    if (finalDayStartTimeUtc) {
        const finalDayStartTime = new Date(finalDayStartTimeUtc);
        if (!Number.isNaN(finalDayStartTime.getTime()))
            return now >= finalDayStartTime;
    }

    const finalProgramDate = finalDailySegment?.date;
    if (!finalProgramDate)
        return true;

    const currentDate = normalizeCalendarDate(now);
    const finalDate = normalizeCalendarDate(finalProgramDate);
    if (!currentDate || !finalDate)
        return true;

    return currentDate >= finalDate;
};

export const shouldCheckProgramCompletionForCreditRequest = ({
    programFormat,
    isGroupcast,
    encryptedRegistrationId,
}: Pick<GetRequestCreditViewStateProps, 'programFormat' | 'isGroupcast' | 'encryptedRegistrationId'>) => programFormat === 'OD Multi-Segment'
    && !isGroupcast
    && Boolean(encryptedRegistrationId);

export const getEndOfVideoViewState = ({
    programFormat,
    hasNextSegment,
    isOnFinalLiveProgramDay,
}: GetEndOfVideoViewStateProps): EndOfVideoViewState => programFormat === 'Live'
    && !hasNextSegment
    && !isOnFinalLiveProgramDay
    ? 'live-return-later'
    : 'standard';

export const getRequestCreditViewState = ({
    programFormat,
    isGroupcast,
    encryptedRegistrationId,
    isCreditProgressError,
    isCreditProgressLoading,
    timeRecords,
}: GetRequestCreditViewStateProps): RequestCreditViewState => {
    const shouldCheckProgramCompletion = shouldCheckProgramCompletionForCreditRequest({
        programFormat,
        isGroupcast,
        encryptedRegistrationId,
    });

    if (!shouldCheckProgramCompletion)
        return 'ready';
    if (isCreditProgressError)
        return 'credit-progress-error';
    if (isCreditProgressLoading)
        return 'loading-credit-progress';
    return hasIncompleteProgramCredit(timeRecords) ? 'incomplete-program' : 'ready';
};

export const useEndOfVideo = (): IEndOfVideoProps => {
    const user = useAppSelector((state) => state.user);
    const launchDetails = useAppSelector((state) => state.player.launchDetails);
    const requestCreditRegistrationId = useAppSelector((state) => state.requestCredit.encryptedRegistrationId);
    const encryptedRegistrationId = requestCreditRegistrationId ?? launchDetails.encryptedRegistrationId ?? undefined;
    const { programDetails } = useProgramDetails(launchDetails?.itemPk ?? '0');
    const currentSegmentItemSk = useAppSelector((state) => state.player.currentSegmentItemSk);
    const { data } = useGetCreditProgressMessagesQuery();
    const { exams, areExamsPassed } = useCpeExams();

    const isSingleSegment = (launchDetails.segments?.length ?? 0) <= 1;
    const programFormat: NonNullable<IEndOfVideoProps['programFormat']> = launchDetails.isWebcast
        ? 'Live'
        : isSingleSegment
            ? (exams?.length ? 'CPE Segment' : 'OD Single-Segment')
            : 'OD Multi-Segment';
    const isOnFinalLiveProgramDay = programFormat !== 'Live'
        || computeIsFinalLiveProgramDay(programDetails?.dailySegments);

    const currentSegment = useMemo(() => {
        if (programFormat === 'Live')
            return undefined;

        if (programFormat === 'OD Single-Segment' || programFormat === 'CPE Segment') {
            if (launchDetails.video) {
                const segment: LaunchSegment = {
                    itemSk: launchDetails.itemSk,
                    itemPk: launchDetails.itemPk,
                    title: launchDetails.title,
                };
                return segment;
            }

            return launchDetails.segments?.[0];
        }

        return launchDetails.segments?.find(segment => segment.itemSk === currentSegmentItemSk);
    }, [
        currentSegmentItemSk,
        launchDetails.itemPk,
        launchDetails.itemSk,
        launchDetails.segments,
        launchDetails.title,
        launchDetails.video,
        programFormat,
    ]);

    const nextSegment = useMemo(() => {
        if (programFormat === 'Live' || programFormat === 'OD Single-Segment' || !launchDetails.segments)
            return undefined;

        const currentSegmentIndex = launchDetails.segments.findIndex(segment => segment.itemSk === currentSegmentItemSk);
        if (currentSegmentIndex < 0)
            return undefined;

        return currentSegmentIndex + 1 < launchDetails.segments.length
            ? launchDetails.segments[currentSegmentIndex + 1]
            : undefined;
    }, [currentSegmentItemSk, launchDetails.segments, programFormat]);

    const {
        data: cleTimeRecords,
        isError: isCreditProgressError,
        isFetching: isCreditProgressFetching,
        isLoading: isCreditProgressLoading,
    } = useGetCleTimeRecordsQuery(
        !user.isGroupcast && programFormat === 'OD Multi-Segment' && encryptedRegistrationId
            ? encryptedRegistrationId
            : skipToken
    );

    const viewState = getEndOfVideoViewState({
        programFormat,
        hasNextSegment: Boolean(nextSegment),
        isOnFinalLiveProgramDay,
    });
    const requestCreditViewState = getRequestCreditViewState({
        programFormat,
        isGroupcast: user.isGroupcast,
        encryptedRegistrationId,
        isCreditProgressError,
        isCreditProgressLoading: isCreditProgressLoading || isCreditProgressFetching,
        timeRecords: cleTimeRecords,
    });

    const title = useMemo(() => {
        const segmentFromProgramFinishedTitle = data?.endOfSegmentHeaderForFullProgram ?? creditProgressMessagesDefaults.endOfSegmentHeaderForFullProgram;
        const singleSegmentFinishedTitle = data?.endOfSegmentHeaderForSingleSegment ?? creditProgressMessagesDefaults.endOfSegmentHeaderForSingleSegment;
        const liveProgramFinishedTitle = data?.endOfLiveProgramHeader ?? creditProgressMessagesDefaults.endOfLiveProgramHeader;
        const multiDayLiveNotFinishedTitle = data?.endOfDayOngoingLiveProgramHeader ?? creditProgressMessagesDefaults.endOfDayOngoingLiveProgramHeader;
        const cpeSegmentExamNotPassedTitle = data?.endOfSegmentCPEExamNotPassedHeader ?? creditProgressMessagesDefaults.endOfSegmentCPEExamNotPassedHeader;
        const cpeSegmentExamPassedTitle = data?.endOfSegmentCPEExamPassedHeader ?? creditProgressMessagesDefaults.endOfSegmentCPEExamPassedHeader;

        switch (programFormat) {
            case 'Live': {
                return viewState === 'live-return-later'
                    ? multiDayLiveNotFinishedTitle
                    : liveProgramFinishedTitle;
            }
            case 'OD Single-Segment':
                return singleSegmentFinishedTitle;
            case 'OD Multi-Segment':
                return segmentFromProgramFinishedTitle;
            case 'CPE Segment':
                return areExamsPassed ? cpeSegmentExamPassedTitle : cpeSegmentExamNotPassedTitle;
        }
    }, [
        areExamsPassed,
        data?.endOfDayOngoingLiveProgramHeader,
        data?.endOfLiveProgramHeader,
        data?.endOfSegmentCPEExamNotPassedHeader,
        data?.endOfSegmentCPEExamPassedHeader,
        data?.endOfSegmentHeaderForFullProgram,
        data?.endOfSegmentHeaderForSingleSegment,
        programFormat,
        viewState,
    ]);

    const nonTitleCTA = programFormat === 'OD Multi-Segment'
        ? data?.endOfODProgramCTA ?? creditProgressMessagesDefaults.endOfODProgramCTA
        : undefined;
    const autoplay = programFormat === 'OD Multi-Segment' ? !launchDetails.isPromoted : undefined;

    return useMemo(() => {
        const incompleteProgram = {
            title: data?.endOfODIncompleteProgramHeader ?? creditProgressMessagesDefaults.endOfODIncompleteProgramHeader,
            body: data?.endOfODIncompleteProgramMessage ?? creditProgressMessagesDefaults.endOfODIncompleteProgramMessage,
            prompt: data?.endOfODIncompleteProgramPrompt ?? creditProgressMessagesDefaults.endOfODIncompleteProgramPrompt,
        };
        const buttonText = {
            requestCredit: data?.requestCreditButtonText ?? creditProgressMessagesDefaults.requestCreditButtonText,
            requestCreditAnyway: data?.endOfODIncompleteProgramRequestCreditButtonText ?? creditProgressMessagesDefaults.endOfODIncompleteProgramRequestCreditButtonText,
            viewSegments: data?.endOfODIncompleteProgramViewSegmentsButtonText ?? creditProgressMessagesDefaults.endOfODIncompleteProgramViewSegmentsButtonText,
            takeCpeExam: data?.takeCPEExamButtonText ?? creditProgressMessagesDefaults.takeCPEExamButtonText,
            dontTakeCpeExam: data?.dontTakeCPEExamButtonText ?? creditProgressMessagesDefaults.dontTakeCPEExamButtonText,
            playNext: data?.playNextButtonText ?? creditProgressMessagesDefaults.playNextButtonText,
        };
        const commonProps = {
            title,
            programFormat,
            currentSegment,
            nextSegment,
            nonTitleCta: nonTitleCTA,
            autoplay,
            buttonText,
            areExamsPassed: exams?.length ? areExamsPassed : true,
            isGroupcast: user.isGroupcast,
            segments: launchDetails.segments ?? undefined,
            cleTimeRecords,
            infoTooltip: data?.endOfSegmentTooltip ?? undefined,
        };

        if (requestCreditViewState === 'incomplete-program') {
            return {
                ...commonProps,
                viewState,
                requestCreditViewState,
                incompleteProgram,
            };
        }

        return {
            ...commonProps,
            viewState,
            requestCreditViewState,
        };
    }, [
        areExamsPassed,
        autoplay,
        currentSegment,
        exams?.length,
        launchDetails.segments,
        nextSegment,
        nonTitleCTA,
        programFormat,
        title,
        data?.dontTakeCPEExamButtonText,
        data?.endOfODIncompleteProgramHeader,
        data?.endOfODIncompleteProgramMessage,
        data?.endOfODIncompleteProgramPrompt,
        data?.endOfODIncompleteProgramRequestCreditButtonText,
        data?.endOfODIncompleteProgramViewSegmentsButtonText,
        data?.requestCreditButtonText,
        data?.takeCPEExamButtonText,
        data?.playNextButtonText,
        data?.endOfSegmentTooltip,
        user.isGroupcast,
        viewState,
        requestCreditViewState,
        cleTimeRecords,
    ]);
};

export default useEndOfVideo;
